import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Reveal from "../components/Reveal";
import SEO from "../components/SEO";
import { CATEGORIES, PRODUCTS, formatPrice } from "../lib/pulaar";

const STATUS_TONE = {
  available: "text-[#2F6B3F]",
  soon: "text-[#5C4033]",
};

export default function Vestiaire() {
  const [activeCat, setActiveCat] = useState("tous");
  const { t } = useTranslation();

  const filtered = useMemo(() => {
    if (activeCat === "tous") return PRODUCTS;
    return PRODUCTS.filter((p) => p.category === activeCat);
  }, [activeCat]);

  const catLabel = (slug) => {
    if (slug === "tous") return t("vestiaire.filterAll");
    return t(`vestiaire.filterCategory.${slug}`, { defaultValue: slug });
  };

  return (
    <main
      data-testid="vestiaire-page"
      className="relative pt-32 md:pt-40 pb-24 bg-[#FBF8EF]"
    >
      <SEO
        title="Vestiaire"
        description="Le vestiaire Pulaar — 9 pièces éditoriales, taille unique, confection à la main. Tops, chemises, vestes, pantalons. Saison 01 bientôt."
        path="/vestiaire"
      />
      <section className="mx-auto max-w-[1600px] px-6 md:px-12 lg:px-16 mb-12 md:mb-16">
        <Reveal>
          <p className="text-[11px] tracking-[0.32em] uppercase text-[#5C4033] mb-5">
            {t("vestiaire.eyebrow")}
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <h1 className="font-serif text-[40px] md:text-[68px] lg:text-[88px] leading-[0.95] tracking-[-0.02em] text-[#121212]">
            {t("vestiaire.title")}
          </h1>
        </Reveal>
        <Reveal delay={0.18}>
          <p className="mt-6 max-w-xl text-sm md:text-base leading-[1.75] text-[#4A4641]">
            {t("vestiaire.subtitle")}
          </p>
        </Reveal>
      </section>

      {/* Filters */}
      <section className="border-y border-[#E5DFD3] sticky top-16 md:top-20 z-30 bg-[#FBF8EF]/85 backdrop-blur-md">
        <div className="mx-auto max-w-[1600px] px-6 md:px-12 lg:px-16 flex items-center gap-2 md:gap-4 overflow-x-auto py-4">
          {CATEGORIES.map((c) => (
            <button
              key={c.slug}
              onClick={() => setActiveCat(c.slug)}
              data-testid={`filter-${c.slug}`}
              className={`shrink-0 inline-flex items-center h-9 px-4 text-[10px] tracking-[0.28em] uppercase border transition-colors duration-300 ${
                activeCat === c.slug
                  ? "bg-[#121212] text-[#FBF8EF] border-[#121212]"
                  : "border-[#E5DFD3] text-[#121212] hover:border-[#5C4033]"
              }`}
            >
              {catLabel(c.slug)}
            </button>
          ))}
          <span className="ml-auto hidden md:inline text-[10px] tracking-[0.28em] uppercase text-[#4A4641]">
            {t("vestiaire.counter", { count: filtered.length })}
          </span>
        </div>
      </section>

      {/* Grid */}
      <section className="mx-auto max-w-[1600px] px-6 md:px-12 lg:px-16 mt-12 md:mt-16">
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-5 md:gap-x-8 gap-y-14">
          {filtered.map((p, i) => (
            <Reveal key={p.slug} delay={(i % 3) * 0.07}>
              <Link
                to={`/vestiaire/${p.slug}`}
                data-testid={`product-card-${p.slug}`}
                className="group block"
              >
                <div className="relative overflow-hidden aspect-[3/4] bg-[#F3EFE7]">
                  <img
                    src={p.image}
                    alt={p.name}
                    loading="lazy"
                    draggable="false"
                    onContextMenu={(e) => e.preventDefault()}
                    className="w-full h-full object-cover transition-all duration-[1400ms] ease-out group-hover:scale-[1.02] blur-[18px] pointer-events-none select-none"
                  />
                  {p.status === "soon" && (
                    <span
                      data-testid={`product-soon-badge-${p.slug}`}
                      className="absolute top-4 left-4 px-3 py-1.5 bg-[#FBF8EF]/90 backdrop-blur-sm text-[10px] tracking-[0.32em] uppercase text-[#5C4033]"
                    >
                      {t("product.statusSoon")}
                    </span>
                  )}
                </div>
                <div className="mt-4 flex items-baseline justify-between gap-3 blur-[14px] opacity-70 select-none">
                  <h3 className="font-serif text-xl md:text-2xl text-[#121212] leading-tight">
                    {p.name}
                  </h3>
                  <span className="font-sans text-sm text-[#4A4641]">
                    {formatPrice(p.price)}
                  </span>
                </div>
                <p
                  className={`mt-2 text-[10px] tracking-[0.28em] uppercase blur-[12px] opacity-70 select-none ${STATUS_TONE[p.status]}`}
                  data-testid={`product-status-${p.slug}`}
                >
                  {t(`product.status${p.status.charAt(0).toUpperCase() + p.status.slice(1).replace("_o", "O")}`, {
                    defaultValue: p.status,
                  })}
                </p>
              </Link>
            </Reveal>
          ))}
        </div>

        {filtered.length === 0 && (
          <p className="text-center text-[#4A4641] py-24">
            {t("vestiaire.empty", { defaultValue: "Aucune pièce dans cette catégorie pour le moment." })}
          </p>
        )}
      </section>
    </main>
  );
}
